import {
  prisma,
  type Prisma,
  type BookableItem,
  type Entity,
  type EntityTier,
  type BookableItemTranslations,
  type Design,
  type Zone,
  type ParticipantGroup,
  type Invitation,
  type NotificationTemplate,
  type FormEntity,
  type Event,
  type Ticket,
  BookableItemConfigSource,
  BookableItemType,
} from "client";

// ============================================================================
// TYPES
// ============================================================================

type BookableItemIncludeOptions = {
  translations?: boolean;
  designs?: boolean;
  zones?: boolean;
  participantGroups?: boolean;
  invitations?: boolean;
  notificationTemplates?: boolean;
  formEntities?: boolean;
  event?: boolean;
  ticket?: boolean;
  entityTier?: boolean;
  entity?: boolean;
};

type BookableItemWithIncludes<T extends BookableItemIncludeOptions> =
  BookableItem & {
    translations?: T["translations"] extends true
      ? BookableItemTranslations[]
      : never;
    designs?: T["designs"] extends true ? Design[] : never;
    zones?: T["zones"] extends true ? Zone[] : never;
    participantGroups?: T["participantGroups"] extends true
      ? ParticipantGroup[]
      : never;
    invitations?: T["invitations"] extends true ? Invitation[] : never;
    notificationTemplates?: T["notificationTemplates"] extends true
      ? NotificationTemplate[]
      : never;
    formEntities?: T["formEntities"] extends true ? FormEntity[] : never;
    event?: T["event"] extends true ? Event : never;
    ticket?: T["ticket"] extends true ? Ticket : never;
    entityTier?: T["entityTier"] extends true ? EntityTier : never;
    entity?: T["entity"] extends true ? Entity : never;
  };

// Type for select results
type BookableItemWithSelect<S extends Prisma.BookableItemSelect> =
  Prisma.BookableItemGetPayload<{ select: S }>;

// ============================================================================
// CREATE
// ============================================================================

/**
 * Create a new BookableItem (SOURCE OF TRUTH for Tickets and EntityTiers)
 */
export const createBookableItem = async (
  data: Prisma.BookableItemCreateInput,
  tx?: Prisma.TransactionClient
): Promise<BookableItem> => {
  const db = tx || prisma;

  try {
    return await db.bookableItem.create({ data });
  } catch (error: any) {
    throw new Error(`Failed to create BookableItem: ${error.message}`);
  }
};

// ============================================================================
// READ
// ============================================================================

/**
 * Get a single BookableItem with optional relations
 * @overload - With select
 */
export async function getBookableItem<S extends Prisma.BookableItemSelect>(
  id: string,
  options: { select: S }
): Promise<BookableItemWithSelect<S> | null>;

/**
 * @overload - With include
 */
export async function getBookableItem<T extends BookableItemIncludeOptions>(
  id: string,
  options: { include: T }
): Promise<BookableItemWithIncludes<T> | null>;

/**
 * @overload - No options
 */
export async function getBookableItem(id: string): Promise<BookableItem | null>;

/**
 * Implementation
 */
export async function getBookableItem<
  S extends Prisma.BookableItemSelect,
  T extends BookableItemIncludeOptions
>(
  id: string,
  options?: {
    select?: S;
    include?: T;
  }
): Promise<
  BookableItemWithSelect<S> | BookableItemWithIncludes<T> | BookableItem | null
> {
  try {
    if (options?.select) {
      return (await prisma.bookableItem.findUnique({
        where: { id },
        select: options.select,
      })) as unknown as BookableItemWithSelect<S>;
    }

    if (options?.include) {
      return (await prisma.bookableItem.findUnique({
        where: { id },
        include: options.include,
      })) as BookableItemWithIncludes<T>;
    }

    return await prisma.bookableItem.findUnique({ where: { id } });
  } catch (error: any) {
    throw new Error(`Failed to get BookableItem: ${error.message}`);
  }
}

/**
 * Get all BookableItems for an event with optional relations
 * @overload - With select
 */
export async function getBookableItemsByEvent<
  S extends Prisma.BookableItemSelect
>(
  eventId: string,
  options: { type?: BookableItemType; select: S }
): Promise<Array<BookableItemWithSelect<S>>>;

/**
 * @overload - With include
 */
export async function getBookableItemsByEvent<
  T extends BookableItemIncludeOptions
>(
  eventId: string,
  options: { type?: BookableItemType; include: T }
): Promise<Array<BookableItemWithIncludes<T>>>;

/**
 * @overload - No options or only type filter
 */
export async function getBookableItemsByEvent(
  eventId: string,
  options?: { type?: BookableItemType }
): Promise<Array<BookableItem>>;

/**
 * Implementation
 */
export async function getBookableItemsByEvent<
  S extends Prisma.BookableItemSelect,
  T extends BookableItemIncludeOptions
>(
  eventId: string,
  options?: {
    type?: BookableItemType;
    select?: S;
    include?: T;
  }
): Promise<
  | Array<BookableItemWithSelect<S>>
  | Array<BookableItemWithIncludes<T>>
  | Array<BookableItem>
> {
  try {
    const where: Prisma.BookableItemWhereInput = {
      eventId,
      ...(options?.type && { type: options.type }),
    };

    if (options?.select) {
      return (await prisma.bookableItem.findMany({
        where,
        select: options.select,
        orderBy: { createdAt: "desc" },
      })) as unknown as Array<BookableItemWithSelect<S>>;
    }

    if (options?.include) {
      return (await prisma.bookableItem.findMany({
        where,
        include: options.include,
        orderBy: { createdAt: "desc" },
      })) as Array<BookableItemWithIncludes<T>>;
    }

    return await prisma.bookableItem.findMany({
      where,
      orderBy: { createdAt: "desc" },
    });
  } catch (error: any) {
    throw new Error(`Failed to get BookableItems by event: ${error.message}`);
  }
}

/**
 * Get effective BookableItem for an Entity (based on configSource)
 * Returns either entity.bookableItem or entity.entityTier.bookableItem
 */
export const getEffectiveBookableItem = async (
  entityId: string
): Promise<BookableItem | null> => {
  try {
    const entity = await prisma.entity.findUnique({
      where: { id: entityId },
      select: {
        configSource: true,
        bookableItem: true,
        entityTier: {
          select: {
            bookableItem: true,
          },
        },
      },
    });

    if (!entity) return null;

    return entity.configSource === BookableItemConfigSource.ENTITY
      ? entity.bookableItem
      : entity.entityTier.bookableItem;
  } catch (error: any) {
    throw new Error(`Failed to get effective BookableItem: ${error.message}`);
  }
};

/**
 * Get effective BookableItem ID for an Entity
 */
export const getEffectiveBookableItemId = async (
  entityId: string
): Promise<string | null> => {
  try {
    const entity = await prisma.entity.findUnique({
      where: { id: entityId },
      select: {
        configSource: true,
        bookableItemId: true,
        entityTier: {
          select: {
            bookableItemId: true,
          },
        },
      },
    });

    if (!entity) return null;

    return entity.configSource === BookableItemConfigSource.ENTITY
      ? entity.bookableItemId
      : entity.entityTier.bookableItemId;
  } catch (error: any) {
    throw new Error(
      `Failed to get effective BookableItem ID: ${error.message}`
    );
  }
};

// ============================================================================
// UPDATE
// ============================================================================

/**
 * Update a BookableItem
 */
export async function updateBookableItem(
  id: string,
  data: Prisma.BookableItemUpdateInput,
  tx?: Prisma.TransactionClient
) {
  try {
    const db = tx || prisma;
    return await db.bookableItem.update({ where: { id }, data });
  } catch (error: any) {
    throw new Error(`Failed to update BookableItem: ${error.message}`);
  }
}

// ============================================================================
// DELETE
// ============================================================================

/**
 * Soft delete a BookableItem and its associated entities in a single transaction
 * Handles Ticket, Entity, and EntityTier soft deletion
 */
export const deleteBookableItem = async (id: string): Promise<void> => {
  try {
    await prisma.$transaction(async (tx) => {
      const bookableItem = await tx.bookableItem.findUnique({
        where: { id },
        include: {
          ticket: { select: { id: true, deletedAt: true } },
          entityTier: {
            select: {
              id: true,
              deletedAt: true,
              entities: { select: { id: true } },
            },
          },
          entities: { select: { id: true, deletedAt: true } },
        },
      });

      if (!bookableItem) {
        throw new Error("BookableItem not found");
      }

      const now = new Date();

      // Soft delete Ticket if exists and not already deleted
      if (bookableItem.ticket && !bookableItem.ticket.deletedAt) {
        await tx.ticket.update({
          where: { id: bookableItem.ticket.id },
          data: { deletedAt: now },
        });
      }

      // Soft delete EntityTier and all its entities if exists
      if (bookableItem.entityTier && !bookableItem.entityTier.deletedAt) {
        // Delete all entities in this tier
        const entityIds = bookableItem.entityTier.entities.map((e) => e.id);
        if (entityIds.length > 0) {
          await tx.entity.updateMany({
            where: { id: { in: entityIds } },
            data: { deletedAt: now },
          });
        }

        // Delete entity tier
        await tx.entityTier.update({
          where: { id: bookableItem.entityTier.id },
          data: { deletedAt: now },
        });
      }

      // Soft delete individual Entity if exists
      if (bookableItem.entities) {
        const entityIds = bookableItem.entities
          .filter((e) => !e.deletedAt)
          .map((e) => e.id);
        if (entityIds.length > 0) {
          await tx.entity.updateMany({
            where: { id: { in: entityIds } },
            data: { deletedAt: now },
          });
        }
      }

      // Soft delete the BookableItem itself
      await tx.bookableItem.update({
        where: { id },
        data: { deletedAt: now },
      });
    });
  } catch (error: any) {
    throw new Error(`Failed to delete BookableItem: ${error.message}`);
  }
};

// ============================================================================
// CLONE (for Entity overrides)
// ============================================================================

/**
 * Clone a BookableItem for type-to-type cloning (including all relations)
 *
 * Clones all scalar fields and relations (participantGroups, zones, designs,
 * notificationTemplates, invitations) from the source BookableItem.
 *
 * Supported cloning patterns:
 * - ENTITY_TIER → ENTITY_TIER (new tier from existing tier)
 * - ENTITY_TIER → ENTITY (entity override from tier)
 * - ENTITY_TIER → TICKET (ticket from tier template)
 * - TICKET → TICKET (new ticket from existing ticket)
 * - ENTITY → ENTITY (new entity from existing entity)
 *
 * @param sourceId - ID of the source BookableItem to clone
 * @param targetType - Type for the cloned BookableItem
 * @returns Newly created BookableItem clone with all relations
 */
export const cloneBookableItem = async (
  sourceId: string,
  targetType: BookableItemType
): Promise<BookableItem> => {
  try {
    const source = await prisma.bookableItem.findUnique({
      where: { id: sourceId },
    });

    if (!source) {
      throw new Error("Source BookableItem not found");
    }

    // Validation: ENTITY type can only be created from ENTITY_TIER source
    if (targetType === BookableItemType.ENTITY) {
      if (source.type !== BookableItemType.ENTITY_TIER) {
        throw new Error(
          "ENTITY type can only be cloned from ENTITY_TIER source"
        );
      }
    }

    // Clone without: ID, timestamps, type-specific foreign keys, and deletedAt
    // BUT keep all relation IDs (participantGroupIds, zoneIds, etc.)
    const {
      id,
      createdAt,
      updatedAt,
      deletedAt,
      // Remove type-specific foreign keys
      ticketId,
      entityTierId,
      ...cloneData
    } = source;

    return await prisma.bookableItem.create({
      data: {
        ...cloneData,
        type: targetType,
      },
    });
  } catch (error: any) {
    throw new Error(`Failed to clone BookableItem: ${error.message}`);
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get BookableItem IDs from ticket IDs
 * @param ticketIds - Array of ticket IDs
 * @returns Array of BookableItem IDs
 */
export const getBookableItemIdsFromTicketIds = async (
  ticketIds: string[]
): Promise<string[]> => {
  try {
    const bookableItems = await prisma.bookableItem.findMany({
      where: {
        ticket: { id: { in: ticketIds } },
        type: BookableItemType.TICKET,
      },
      select: {
        id: true,
      },
    });

    return bookableItems.map((item) => item.id);
  } catch (error: any) {
    throw new Error(
      `Failed to get BookableItem IDs from ticket IDs: ${error.message}`
    );
  }
};

/**
 * Get all BookableItems that have a specific design attached
 * @param designId - The Design ID
 */
export const getBookableItemsByDesign = async (
  designId: string
): Promise<BookableItem[]> => {
  try {
    const bookableItems = await prisma.bookableItem.findMany({
      where: { designIds: { has: designId } },
    });
    return bookableItems;
  } catch (error: any) {
    throw new Error(`Failed to get BookableItems by design: ${error.message}`);
  }
};

/**
 * Detach design from multiple BookableItems at once
 * @param designId - The Design ID to detach
 * @param bookableItemIds - Array of BookableItem IDs
 */
export const detachDesignFromMultipleBookableItems = async (
  designId: string,
  bookableItemIds: string[]
): Promise<boolean> => {
  try {
    // Get all BookableItems and update them individually
    const bookableItems = await prisma.bookableItem.findMany({
      where: { id: { in: bookableItemIds } },
      select: { id: true, designIds: true },
    });

    for (const bookableItem of bookableItems) {
      const updatedDesignIds = bookableItem.designIds.filter(
        (id) => id !== designId
      );
      await prisma.bookableItem.update({
        where: { id: bookableItem.id },
        data: { designIds: updatedDesignIds },
      });
    }
    return true;
  } catch (error: any) {
    throw new Error(
      `Failed to detach design from multiple BookableItems: ${error.message}`
    );
  }
};

// ============================================================================
// RELATION OPERATIONS (for many-to-many relations)
// ============================================================================

/**
 * Add a design to BookableItem using many-to-many connect
 */
export const addDesignToBookableItem = async (
  bookableItemId: string,
  designId: string
): Promise<BookableItem> => {
  try {
    return await prisma.bookableItem.update({
      where: { id: bookableItemId },
      data: {
        designs: {
          connect: { id: designId },
        },
      },
    });
  } catch (error: any) {
    throw new Error(`Failed to add design to BookableItem: ${error.message}`);
  }
};

/**
 * Remove a design from BookableItem using many-to-many disconnect
 */
export const removeDesignFromBookableItem = async (
  bookableItemId: string,
  designId: string
): Promise<BookableItem> => {
  try {
    return await prisma.bookableItem.update({
      where: { id: bookableItemId },
      data: {
        designs: {
          disconnect: { id: designId },
        },
      },
    });
  } catch (error: any) {
    throw new Error(
      `Failed to remove design from BookableItem: ${error.message}`
    );
  }
};

/**
 * Add a zone to BookableItem using many-to-many connect
 */
export const addZoneToBookableItem = async (
  bookableItemId: string,
  zoneId: string
): Promise<BookableItem> => {
  try {
    return await prisma.bookableItem.update({
      where: { id: bookableItemId },
      data: {
        zones: {
          connect: { id: zoneId },
        },
      },
    });
  } catch (error: any) {
    throw new Error(`Failed to add zone to BookableItem: ${error.message}`);
  }
};

/**
 * Remove a zone from BookableItem using many-to-many disconnect
 */
export const removeZoneFromBookableItem = async (
  bookableItemId: string,
  zoneId: string
): Promise<BookableItem> => {
  try {
    return await prisma.bookableItem.update({
      where: { id: bookableItemId },
      data: {
        zones: {
          disconnect: { id: zoneId },
        },
      },
    });
  } catch (error: any) {
    throw new Error(
      `Failed to remove zone from BookableItem: ${error.message}`
    );
  }
};

export const getBookableItemByTicketId = async (
  ticketId: string,
  options?: {
    select?: Prisma.BookableItemSelect;
    include?: Prisma.BookableItemInclude;
  }
) => {
  try {
    return await prisma.bookableItem.findFirst({
      where: { ticket: { id: ticketId } },
      ...(options?.select && { select: options.select }),
      ...(options?.include && { include: options.include }),
    });
  } catch (error: any) {
    throw new Error(
      `Failed to get BookableItem by Ticket ID: ${error.message}`
    );
  }
};
